import { useAppSelector } from 'app/hooks';
import { SizingItemsType } from 'data/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fineTuningValuesDifferFromRecommended,
  getVisibleFineTuningComparableItems
} from 'utils/fine-tuning-defaults-changed';
import { fetchRecommendedSizingDefaults } from 'utils/recommended-sizing-defaults';

export default function useHasFineTuningDefaultChanges() {
  const defaultSizingItems = useAppSelector(
    (state) => state.data.defaultSizingItems
  );
  const modifiedDefaultCategories = useAppSelector(
    (state) => state.data.modifiedDefaultCategories
  );
  const structure = useAppSelector((state) => state.structure.structure);
  const projectSettings = useAppSelector(
    (state) => state.project.projectSettings
  );
  const yearBuiltFromAttom = useAppSelector(
    (state) => state.project.attomData?.summary?.yearBuilt
  );
  const climateZone =
    useAppSelector((state) => state.project.locationData?.climateZone) || '4';

  const yearBuilt =
    projectSettings.find((setting) => setting.code === 'year_built')?.value ||
    yearBuiltFromAttom;

  const comparableItems = useMemo(
    () => getVisibleFineTuningComparableItems(structure),
    [structure]
  );

  const [recommended, setRecommended] = useState<SizingItemsType | null>(null);
  const [refreshId, setRefreshId] = useState(0);
  const defaultSizingItemsRef = useRef(defaultSizingItems);
  defaultSizingItemsRef.current = defaultSizingItems;

  const refreshRecommended = useCallback(() => {
    setRefreshId((current) => current + 1);
  }, []);

  useEffect(() => {
    if (comparableItems.length === 0) {
      setRecommended(null);
      return undefined;
    }

    let cancelled = false;

    fetchRecommendedSizingDefaults(defaultSizingItemsRef.current, {
      yearBuilt,
      climateZone
    })
      .then((result) => {
        if (!cancelled) {
          setRecommended(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRecommended(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [yearBuilt, climateZone, refreshId, comparableItems]);

  const hasModifiedCategories = Object.values(modifiedDefaultCategories).some(
    Boolean
  );

  const hasChanges = useMemo(() => {
    if (comparableItems.length === 0) {
      return false;
    }

    if (!recommended) {
      return hasModifiedCategories;
    }

    return fineTuningValuesDifferFromRecommended(
      defaultSizingItems,
      recommended,
      comparableItems
    );
  }, [comparableItems, recommended, hasModifiedCategories, defaultSizingItems]);

  return { hasChanges, refreshRecommended };
}
